import { useCallback, useMemo } from 'react';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import {
  resetProducts,
  fetchProductsData,
  fetchOneProduct,
  updateProducts,
} from 'src/redux/slice/product';
// hook
import useCheckout from 'src/hooks/use-checkout';
// auth
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export default function useProducts() {
  const dispatch = useDispatch();
  const { onUpdateItems } = useCheckout();
  const { user } = useAuthContext();

  const idUser = user?.id;
  const config = user?.configSelected?.id;

  const { products, isLoading, isValidating, error, nextPage, fieldsSearchable } = useSelector(
    (state) => state.products
  );

  const resetData = useCallback(() => {
    dispatch(resetProducts());
  }, [dispatch]);

  const onRefreshProducts = useCallback(async () => {
    const fetchPage = async (page) => {
      const result = await dispatch(
        fetchProductsData({ idUser, config, page })
      ).unwrap();
      if (result.nextPage) {
        await fetchPage(result.nextPage);
      }
    };
    await fetchPage(nextPage || 1);
  }, [dispatch, idUser, config, nextPage]);

  const onRefreshOneProduct = useCallback(
    async ({ id, idERP }) => {
      if (!idERP) {
        console.warn('[onRefreshOneProduct] idERP is undefined, skipping product refresh');
        return null;
      }
      try {
        const product = await dispatch(fetchOneProduct({ idUser, config, id: idERP })).unwrap();
        onUpdateItems([{ id, available: product.available }]);
        return product;
      } catch (error) {
        console.error('[useProducts] Error refreshing one product:', error);
        return null;
      }
    },
    [dispatch, idUser, config, onUpdateItems]
  );

  const onInitProducts = useCallback(async () => {
    try {
      await dispatch(fetchProductsData({ idUser, config, replaceProducts: true })).unwrap();
    } catch (error) {
      console.error('[useProducts] Error initializing products:', error);
    }
  }, [dispatch, idUser, config]);

  const onUpdateProducts = useCallback(
    (data, fs = fieldsSearchable) => {
      dispatch(updateProducts({ data, fieldsSearchable: fs }));
      const checkoutItems = data.map((item) => ({
        id: item.id,
        price: item.price,
        available: item.available,
      }));
      onUpdateItems(checkoutItems);
    },
    [dispatch, onUpdateItems, fieldsSearchable]
  );

  return useMemo(
    () => ({
      allProducts: products,
      isLoading,
      isValidating,
      error,
      fieldsSearchable,
      onRefreshProducts,
      onInitProducts,
      onResetProducts: resetData,
      onRefreshOneProduct,
      onUpdateProducts,
    }),
    [
      products,
      isLoading,
      isValidating,
      error,
      fieldsSearchable,
      onRefreshProducts,
      onInitProducts,
      resetData,
      onRefreshOneProduct,
      onUpdateProducts,
    ]
  );
}
